import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { ComplianceManager } from './ComplianceManager';

/**
 * ComplianceAdminPanel
 *
 * Admin-only org-wide compliance dashboard. Two views in one component:
 *
 *   1. Overview (default)
 *      • Headline counter cards: Verified, Expiring soon, Expired, Pending
 *        review, Rejected, Missing.
 *      • Per-staff table: name / email / role / progress at-a-glance
 *        ("3 verified · 1 expiring · 2 missing"). Click a row to drill in.
 *
 *   2. Drilldown
 *      • Renders the shared <ComplianceManager mode="staff" /> for the
 *        selected staff member, giving the admin full edit + verify
 *        + reject power. A "Back to overview" button returns to view 1.
 *
 * Data lives in `summary` (org rollup) — re-fetched after any mutation in
 * the drilldown so the headline counters stay accurate.
 */

const STATUS_PILLS = [
  { key: 'verified',             label: 'Verified',       bg: 'hsl(150 60% 92%)', fg: 'hsl(150 60% 25%)' },
  { key: 'expiring_soon',        label: 'Expiring soon',  bg: 'hsl(35 90% 92%)',  fg: 'hsl(30 80% 28%)' },
  { key: 'expired',              label: 'Expired',        bg: 'hsl(0 80% 95%)',   fg: 'hsl(0 70% 35%)' },
  { key: 'pending_verification', label: 'Pending review', bg: 'hsl(220 80% 95%)', fg: 'hsl(220 70% 35%)' },
  { key: 'rejected',             label: 'Rejected',       bg: 'hsl(0 80% 95%)',   fg: 'hsl(0 70% 35%)' },
];

export function ComplianceAdminPanel({ staffProfiles }) {
  const [summary, setSummary] = useState({ by_status: {}, by_staff: [], total_records: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // { staffId, staffName }

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await api.getComplianceSummary();
      setSummary(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Build a quick lookup so we can show staff who have NO records at all
  // (otherwise they'd be invisible — exactly the people who need chasing).
  const profilesById = {};
  (staffProfiles || []).forEach((p) => { profilesById[p._id] = p; });

  // Merge the summary's per-staff buckets with profiles missing records so
  // the table still lists them with a Missing pill.
  const staffRows = (() => {
    const rows = [...(summary.by_staff || [])];
    const seen = new Set(rows.map((r) => r.staff_id));
    (staffProfiles || []).forEach((p) => {
      if (!seen.has(p._id)) {
        rows.push({
          staff_id: p._id,
          full_name: p.user_name || p.user_email || p._id,
          email: p.user_email,
          role: p.user_role,
          employment_type: p.employment_type,
          verified: 0,
          expiring_soon: 0,
          expired: 0,
          pending_verification: 0,
          rejected: 0,
          missing: 0,
          total: 0,
        });
      }
    });
    return rows;
  })();

  if (drilldown) {
    return (
      <div data-testid="compliance-admin-drilldown">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => { setDrilldown(null); refresh(); }}
          style={{ marginBottom: '1rem' }}
          data-testid="compliance-admin-back"
        >
          ← Back to overview
        </button>
        <h3 style={{ marginBottom: '0.25rem' }}>{drilldown.staffName}'s compliance</h3>
        <p style={{ marginTop: 0, color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
          Verify uploaded evidence or reject with feedback. Edits made here update the staff member's view immediately.
        </p>
        <ComplianceManager
          mode="staff"
          staffId={drilldown.staffId}
          staffName={drilldown.staffName}
          onChanged={refresh}
        />
      </div>
    );
  }

  return (
    <div data-testid="compliance-admin-overview">
      {error && (
        <div className="card" style={{ marginBottom: '1rem', borderColor: 'hsl(0 70% 70%)', background: 'hsl(0 70% 97%)' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Headline counters */}
      <div
        style={{ display: 'grid', gap: '0.85rem', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: '1rem' }}
        data-testid="compliance-summary-cards"
      >
        {STATUS_PILLS.map((p) => (
          <div
            key={p.key}
            className="card"
            style={{ background: p.bg, color: p.fg, padding: '0.85rem' }}
            data-testid={`compliance-summary-${p.key}`}
          >
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              {p.label}
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, marginTop: '0.25rem' }}>
              {summary.by_status?.[p.key] || 0}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
        Total compliance records on file: <strong>{summary.total_records}</strong>
        {loading && <em style={{ marginLeft: '0.5rem' }}>refreshing…</em>}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="data-table" style={{ margin: 0 }} data-testid="compliance-staff-table">
          <thead>
            <tr>
              <th>Staff</th>
              <th>Role</th>
              <th>Verified</th>
              <th>Expiring</th>
              <th>Expired</th>
              <th>Pending</th>
              <th>Rejected</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {staffRows.length === 0 && (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>No staff profiles yet.</td></tr>
            )}
            {staffRows.map((r) => {
              const profile = profilesById[r.staff_id];
              const name = r.full_name || profile?.user_name || profile?.user_email || r.staff_id;
              const role = r.role || profile?.user_role || profile?.employment_type || '—';
              return (
                <tr key={r.staff_id} data-testid={`compliance-row-staff-${r.staff_id}`}>
                  <td>
                    <strong>{name}</strong>
                    {r.email && (
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{r.email}</div>
                    )}
                  </td>
                  <td>{role}</td>
                  <td><span style={{ color: 'hsl(150 60% 25%)' }}>{r.verified || 0}</span></td>
                  <td><span style={{ color: 'hsl(30 80% 28%)' }}>{r.expiring_soon || 0}</span></td>
                  <td><span style={{ color: 'hsl(0 70% 35%)' }}>{r.expired || 0}</span></td>
                  <td><span style={{ color: 'hsl(220 70% 35%)' }}>{r.pending_verification || 0}</span></td>
                  <td><span style={{ color: 'hsl(0 70% 35%)' }}>{r.rejected || 0}</span></td>
                  <td>{r.total || 0}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: '0.25rem 0.65rem', fontSize: '0.78rem' }}
                      onClick={() => setDrilldown({ staffId: r.staff_id, staffName: name })}
                      data-testid={`compliance-review-${r.staff_id}`}
                    >
                      Review →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ComplianceAdminPanel;
